import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Loader2, Mail, ShieldAlert, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { BrandButton } from "@/components/brand/brand-button";
import { BrandInput } from "@/components/brand/brand-input";
import { DashboardBottomNav } from "@/components/dashboard/DashboardBottomNav";
import { DashboardProvider } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardTopBar } from "@/components/dashboard/DashboardTopBar";
import { LoadingScreen } from "@/components/LoadingScreen";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
	ConfirmDeleteDialogTrigger,
} from "@/components/ui/confirm-delete-dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { useWebHaptics } from "@/lib/haptics";
import { toast } from "@/lib/toast";
import {
	currentPasswordSchema,
	displayNameSchema,
	emailSchema,
	passwordSchema,
} from "@/lib/validation";
import { useSession, useUpdateUser } from "../hooks/auth-hooks";
import { authClient, signOut } from "../lib/auth-client";

export const Route = createFileRoute("/settings")({
	head: () => ({
		meta: [
			{ title: "Settings - AgentMRR" },
			{ name: "robots", content: "noindex, nofollow" },
		],
	}),
	component: SettingsPage,
});

function SettingsPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();

	if (isPending) {
		return <LoadingScreen />;
	}

	if (!session || !user) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<div className="max-w-md text-center space-y-4">
					<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; Auth required
					</p>
					<p className="text-base text-muted-foreground">
						Please log in to access your settings.
					</p>
					<BrandButton
						type="button"
						onClick={() =>
							navigate({ to: "/login", search: { redirect: "/settings" } })
						}
					>
						Log in
					</BrandButton>
				</div>
			</div>
		);
	}

	return <SettingsContent />;
}

function SettingsContent() {
	const { user, refetch } = useSession();
	const navigate = useNavigate();

	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	const queryClient = useQueryClient();
	const digestQuery = useQuery({
		queryKey: ["newsletter-subscription"],
		queryFn: async () => {
			const res = await fetch("/api/newsletter/subscription");
			if (!res.ok) throw new Error("Failed to load subscription");
			const data = (await res.json()) as { isSubscribed: boolean };
			return data.isSubscribed;
		},
		enabled: Boolean(user?.email),
	});

	const digestMutation = useMutation({
		mutationFn: async (subscribe: boolean) => {
			const res = await fetch("/api/newsletter/subscription", {
				method: subscribe ? "POST" : "DELETE",
			});
			if (!res.ok) throw new Error("Failed to update subscription");
			return subscribe;
		},
		onMutate: async (subscribe) => {
			await queryClient.cancelQueries({
				queryKey: ["newsletter-subscription"],
			});
			const previous = queryClient.getQueryData<boolean>([
				"newsletter-subscription",
			]);
			queryClient.setQueryData(["newsletter-subscription"], subscribe);
			return { previous };
		},
		onError: (_err, _subscribe, ctx) => {
			if (ctx?.previous !== undefined) {
				queryClient.setQueryData(["newsletter-subscription"], ctx.previous);
			}
			toast.error("Failed to update subscription");
		},
	});

	const mrrUpdatesQuery = useQuery({
		queryKey: ["mrr-updates-pref"],
		queryFn: async () => {
			const res = await fetch("/api/settings/mrr-updates");
			if (!res.ok) throw new Error("Failed to load preference");
			const data = (await res.json()) as { enabled: boolean };
			return data.enabled;
		},
		enabled: Boolean(user?.email),
	});

	const mrrUpdatesMutation = useMutation({
		mutationFn: async (enabled: boolean) => {
			const res = await fetch("/api/settings/mrr-updates", {
				method: enabled ? "POST" : "DELETE",
			});
			if (!res.ok) throw new Error("Failed to update preference");
			return enabled;
		},
		onMutate: async (enabled) => {
			await queryClient.cancelQueries({ queryKey: ["mrr-updates-pref"] });
			const previous = queryClient.getQueryData<boolean>(["mrr-updates-pref"]);
			queryClient.setQueryData(["mrr-updates-pref"], enabled);
			return { previous };
		},
		onError: (_err, _enabled, ctx) => {
			if (ctx?.previous !== undefined) {
				queryClient.setQueryData(["mrr-updates-pref"], ctx.previous);
			}
			toast.error("Failed to update preference");
		},
	});

	const handleDeleteAccount = async () => {
		if (deleteConfirmText !== "DELETE") return;
		setIsDeleting(true);
		try {
			const result = await authClient.deleteUser();
			if (result.error) {
				toast.error(result.error.message || "Failed to delete account");
			} else {
				setDeleteDialogOpen(false);
				await signOut();
				navigate({ to: "/", search: { unsubscribed: undefined } });
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to delete account",
			);
		} finally {
			setIsDeleting(false);
		}
	};

	return (
		<DashboardProvider>
			<div className="relative min-h-svh bg-background">
				<DashboardSidebar />
				<div className="lg:pl-56">
					<DashboardTopBar />
					<DashboardLayout>
						<DashboardContent className="max-w-3xl">
							<div className="pb-5 mb-5">
								<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
									&gt; Dashboard / settings
								</p>
								<p className="mt-2 text-base text-muted-foreground">
									Manage your account preferences.
								</p>
							</div>

							<div className="space-y-6">
								<ProfileSection
									initialName={user?.name ?? ""}
									onSaved={refetch}
								/>
								<EmailSection
									currentEmail={user?.email ?? ""}
									emailVerified={user?.emailVerified ?? false}
									onSaved={refetch}
								/>
								<PasswordSection />

								<section className="border border-border bg-card">
									<div className="border-b border-border px-5 py-3">
										<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
											&gt; Notifications
										</span>
									</div>
									<div className="space-y-4 p-5">
										<label className="flex items-start gap-3 cursor-pointer">
											<input
												type="checkbox"
												checked={digestQuery.data ?? false}
												disabled={
													digestQuery.isPending || digestMutation.isPending
												}
												onChange={(e) =>
													digestMutation.mutate(e.target.checked)
												}
												className="mt-0.5 size-4 shrink-0 accent-brand disabled:cursor-not-allowed"
											/>
											<span className="space-y-1">
												<span className="block font-mono text-sm uppercase tracking-widest text-foreground">
													Agent market updates
												</span>
												<span className="block text-base text-muted-foreground">
													Follow new AI agents, breakout products, and verified
													momentum.
												</span>
											</span>
										</label>
										<label className="flex items-start gap-3 cursor-pointer">
											<input
												type="checkbox"
												checked={mrrUpdatesQuery.data ?? false}
												disabled={
													mrrUpdatesQuery.isPending ||
													mrrUpdatesMutation.isPending
												}
												onChange={(e) =>
													mrrUpdatesMutation.mutate(e.target.checked)
												}
												className="mt-0.5 size-4 shrink-0 accent-brand disabled:cursor-not-allowed"
											/>
											<span className="space-y-1">
												<span className="block font-mono text-sm uppercase tracking-widest text-foreground">
													Agent MRR updates
												</span>
												<span className="block text-base text-muted-foreground">
													Get an email when your agent crosses a new revenue
													milestone.
												</span>
											</span>
										</label>
									</div>
								</section>

								<section className="border border-[color:var(--ticker-down)]/30 bg-card">
									<div className="border-b border-[color:var(--ticker-down)]/30 px-5 py-3">
										<span className="font-mono text-sm uppercase tracking-[0.14em] text-[color:var(--ticker-down)]">
											&gt; Danger zone
										</span>
									</div>
									<div className="space-y-4 p-5">
										<p className="text-base text-muted-foreground">
											Once you delete your account, all your data will be
											permanently removed. This action cannot be undone.
										</p>
										<ConfirmDeleteDialog
											open={deleteDialogOpen}
											onOpenChange={(open) => {
												setDeleteDialogOpen(open);
												if (!open) setDeleteConfirmText("");
											}}
										>
											<ConfirmDeleteDialogTrigger asChild>
												<BrandButton type="button" variant="destructive">
													<Trash2 className="size-3.5" />
													Delete account
												</BrandButton>
											</ConfirmDeleteDialogTrigger>
											<ConfirmDeleteDialogContent>
												<ConfirmDeleteDialogHeader
													title="Delete Account"
													description="This will permanently delete your account, all your businesses, reviews, and videos. This action cannot be undone."
												/>
												<ConfirmDeleteDialogConfirmation
													value={deleteConfirmText}
													onChange={setDeleteConfirmText}
													confirmText="DELETE"
													placeholder="Type DELETE"
												/>
												<ConfirmDeleteDialogFooter
													onCancel={() => {
														setDeleteDialogOpen(false);
														setDeleteConfirmText("");
													}}
													onConfirm={handleDeleteAccount}
													confirmDisabled={deleteConfirmText !== "DELETE"}
													isLoading={isDeleting}
													confirmLabel="Delete My Account"
												/>
											</ConfirmDeleteDialogContent>
										</ConfirmDeleteDialog>
									</div>
								</section>
							</div>
						</DashboardContent>
					</DashboardLayout>
				</div>
				<DashboardBottomNav />
			</div>
		</DashboardProvider>
	);
}

const profileSchema = z.object({ name: displayNameSchema });
type ProfileForm = z.infer<typeof profileSchema>;

function ProfileSection({
	initialName,
	onSaved,
}: {
	initialName: string;
	onSaved: () => Promise<unknown>;
}) {
	const updateUser = useUpdateUser();
	const { trigger } = useWebHaptics();

	const form = useForm<ProfileForm>({
		resolver: zodResolver(profileSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: { name: initialName },
	});

	useEffect(() => {
		if (initialName && !form.formState.isDirty) {
			form.reset({ name: initialName });
		}
	}, [initialName, form]);

	const onSubmit = async (values: ProfileForm) => {
		try {
			await updateUser.mutateAsync({ name: values.name });
			await onSaved();
			trigger("success");
			toast.success("Profile saved");
			form.reset({ name: values.name });
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update profile",
			);
		}
	};

	const loading = form.formState.isSubmitting;

	return (
		<section className="border border-border bg-card">
			<div className="border-b border-border px-5 py-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Profile
				</span>
			</div>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-4 p-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Display name</FormLabel>
								<FormControl>
									<BrandInput
										autoFocus
										placeholder="Enter your name"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<BrandButton type="submit" disabled={loading}>
						{loading ? (
							<Loader2 className="size-3.5 animate-spin" />
						) : (
							<Check className="size-3.5" />
						)}
						{loading ? "Saving" : "Save profile"}
					</BrandButton>
				</form>
			</Form>
		</section>
	);
}

const emailChangeSchema = z.object({ newEmail: emailSchema });
type EmailChangeForm = z.infer<typeof emailChangeSchema>;

function EmailSection({
	currentEmail,
	emailVerified,
	onSaved,
}: {
	currentEmail: string;
	emailVerified: boolean;
	onSaved: () => Promise<unknown>;
}) {
	const { trigger } = useWebHaptics();
	const [resending, setResending] = useState(false);

	const handleResend = async () => {
		setResending(true);
		try {
			const res = await authClient.sendVerificationEmail({
				email: currentEmail,
				callbackURL: "/",
			});
			if (res.error) {
				toast.error(res.error.message ?? "Failed to send verification email");
				return;
			}
			trigger("success");
			toast.success("Verification email sent. Check your inbox.");
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: "Failed to send verification email",
			);
		} finally {
			setResending(false);
		}
	};

	const form = useForm<EmailChangeForm>({
		resolver: zodResolver(
			emailChangeSchema.refine((data) => data.newEmail !== currentEmail, {
				message: "That's already your current email",
				path: ["newEmail"],
			}),
		),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: { newEmail: "" },
	});

	const onSubmit = async (values: EmailChangeForm) => {
		try {
			const result = await authClient.changeEmail({
				newEmail: values.newEmail,
			});
			if (result.error) {
				form.setError("newEmail", {
					message: result.error.message || "Failed to update email",
				});
				return;
			}
			trigger("success");
			await onSaved();
			form.reset({ newEmail: "" });
			toast.success(
				"Email update requested. Please check your inbox to verify.",
			);
		} catch (err) {
			form.setError("newEmail", {
				message: err instanceof Error ? err.message : "Failed to update email",
			});
		}
	};

	const loading = form.formState.isSubmitting;

	return (
		<section className="border border-border bg-card">
			<div className="border-b border-border px-5 py-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Email
				</span>
			</div>
			<div className="border-b border-border p-5">
				{emailVerified ? (
					<div className="flex items-center gap-2">
						<Check className="size-4 shrink-0 text-[color:var(--ticker-up)]" />
						<p className="text-base text-foreground">Your email is verified.</p>
					</div>
				) : (
					<div className="space-y-3">
						<div className="flex items-center gap-2">
							<ShieldAlert className="size-4 shrink-0 text-[color:var(--ticker-down)]" />
							<p className="text-base text-foreground">
								Your email is not verified.
							</p>
						</div>
						<p className="text-base text-muted-foreground">
							Verifying confirms you own this address. It's optional and does
							not block anything.
						</p>
						<BrandButton
							type="button"
							variant="secondary"
							disabled={resending}
							onClick={handleResend}
						>
							{resending ? (
								<Loader2 className="size-3.5 animate-spin" />
							) : (
								<Mail className="size-3.5" />
							)}
							{resending ? "Sending" : "Resend verification email"}
						</BrandButton>
					</div>
				)}
			</div>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-4 p-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="newEmail"
						render={({ field }) => (
							<FormItem>
								<FormLabel>New email address</FormLabel>
								<FormControl>
									<BrandInput
										type="email"
										placeholder={currentEmail}
										{...field}
									/>
								</FormControl>
								<FormDescription>
									A verification email will be sent to your new address.
								</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>
					<BrandButton type="submit" disabled={loading}>
						{loading ? (
							<Loader2 className="size-3.5 animate-spin" />
						) : (
							<Check className="size-3.5" />
						)}
						{loading ? "Saving" : "Update email"}
					</BrandButton>
				</form>
			</Form>
		</section>
	);
}

const passwordChangeSchema = z
	.object({
		currentPassword: currentPasswordSchema,
		newPassword: passwordSchema,
		confirmPassword: z.string().min(1, "Confirm your new password"),
	})
	.refine((data) => data.newPassword === data.confirmPassword, {
		message: "New passwords do not match",
		path: ["confirmPassword"],
	});

type PasswordChangeForm = z.infer<typeof passwordChangeSchema>;

function PasswordSection() {
	const { trigger } = useWebHaptics();
	const form = useForm<PasswordChangeForm>({
		resolver: zodResolver(passwordChangeSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: {
			currentPassword: "",
			newPassword: "",
			confirmPassword: "",
		},
	});

	const onSubmit = async (values: PasswordChangeForm) => {
		try {
			const result = await authClient.changePassword({
				currentPassword: values.currentPassword,
				newPassword: values.newPassword,
			});
			if (result.error) {
				const message = result.error.message || "Failed to update password";
				if (message.toLowerCase().includes("current")) {
					form.setError("currentPassword", { message });
				} else {
					form.setError("root.serverError", { message });
					toast.error(message);
				}
				return;
			}
			trigger("success");
			toast.success("Password updated");
			form.reset();
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to update password";
			form.setError("root.serverError", { message });
			toast.error(message);
		}
	};

	const loading = form.formState.isSubmitting;

	return (
		<section className="border border-border bg-card">
			<div className="border-b border-border px-5 py-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Password
				</span>
			</div>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-4 p-5"
					noValidate
				>
					<FormField
						control={form.control}
						name="currentPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Current password</FormLabel>
								<FormControl>
									<BrandInput
										type="password"
										autoComplete="current-password"
										placeholder="••••••••"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="newPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>New password</FormLabel>
								<FormControl>
									<BrandInput
										type="password"
										autoComplete="new-password"
										placeholder="min 8 chars"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="confirmPassword"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Confirm new password</FormLabel>
								<FormControl>
									<BrandInput
										type="password"
										autoComplete="new-password"
										placeholder="••••••••"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<BrandButton type="submit" disabled={loading}>
						{loading ? (
							<Loader2 className="size-3.5 animate-spin" />
						) : (
							<Check className="size-3.5" />
						)}
						{loading ? "Saving" : "Update password"}
					</BrandButton>
				</form>
			</Form>
		</section>
	);
}
